import { Button, DialogProps, Link, SvgIconProps, Typography } from '@mui/material'
import React, { useState } from 'react'
import { formatNumber, trimId } from 'src/common/helpers'
import { Modal, SummaryTable } from 'src/ui'
import * as S from './styles'
import { Box } from '@mui/system'
import { FlagIcon, USFlagIcon } from 'src/common/assets'
import { ChainAsset, RequestType, FiatCurrency, ChainName } from 'src/common/static'

interface Asset {
  name: ChainAsset
  Icon: React.FC<SvgIconProps<'svg', Record<string, unknown>>>
}

interface ConfirmRequestProps extends DialogProps {
  confirmRequest: (stopLoading: () => void) => void
  networkFee: number
  asset: Asset
  cryptoAmount: string
  fiatAmount: number | string
  type: RequestType
  fiatCurrency: FiatCurrency
  selectedWalletAddress: string | null
  selectedWalletChain: ChainName
}

const ConfirmRequest: React.FC<ConfirmRequestProps> = ({
  confirmRequest,
  asset,
  networkFee,
  cryptoAmount,
  fiatAmount,
  type,
  fiatCurrency,
  selectedWalletAddress,
  selectedWalletChain,
  ...props
}: ConfirmRequestProps) => {
  const isMint = type === RequestType.MINT
  const isBurn = type === RequestType.BURN

  const [loading, setLoading] = useState(false)

  const stopLoading = () => {
    setLoading(false)
  }

  const handleClick = () => {
    setLoading(true)
    confirmRequest(stopLoading)
  }

  const displayAsset = (
    Icon: React.FC<SvgIconProps<'svg', Record<string, unknown>>>,
    title: string,
    subtitle: string
  ) => (
    <Box display="flex" alignItems="center" width="145px">
      <Icon sx={{ mr: '16px', fontSize: '40px' }} />
      <Box>
        <Typography fontWeight={600}>{title}</Typography>
        <Typography variant="body2">{subtitle}</Typography>
      </Box>
    </Box>
  )

  return (
    <Modal {...props} title={`Confirm ${isMint ? 'Mint' : 'Burn'}`}>
      <S.Row display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <S.DenseRow>
            <Typography>You pay</Typography>
          </S.DenseRow>
          <Typography variant="h4" fontWeight={600}>
            {isMint && fiatAmount}
            {isBurn && formatNumber(cryptoAmount)}
          </Typography>
        </Box>
        {isMint &&
          displayAsset(
            fiatCurrency === FiatCurrency.USD ? USFlagIcon : FlagIcon,
            fiatCurrency,
            `${fiatCurrency} Currency`
          )}
        {isBurn && displayAsset(asset.Icon, asset.name, `${asset.name} Coin`)}
      </S.Row>
      <S.Row display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <S.DenseRow>
            <Typography>You receive</Typography>
          </S.DenseRow>
          <Typography variant="h4" fontWeight={600}>
            {isMint && formatNumber(cryptoAmount)}
            {isBurn && fiatAmount}
          </Typography>
        </Box>
        <Typography fontWeight={600}>
          {isMint && displayAsset(asset.Icon, asset.name, `${asset.name} Coin`)}
          {isBurn &&
            displayAsset(
              fiatCurrency === FiatCurrency.USD ? USFlagIcon : FlagIcon,
              fiatCurrency,
              `${fiatCurrency} Currency`
            )}
        </Typography>
      </S.Row>
      <S.Row>
        <S.Summary>Summary</S.Summary>
        <SummaryTable
          data={[
            {
              key: 'Order Fee',
              value: '$0.00'
            },
            {
              key: isMint ? 'Network Fee' : 'Transfer Fee',
              value: isMint ? `$${formatNumber(networkFee)}` : '$0.00'
            },
            {
              key: isMint ? 'Receiving Blockchain Address' : 'Sender Blockchain Address',
              value: trimId(selectedWalletAddress)
            },
            {
              key: 'Chain',
              value: selectedWalletChain
            }
          ]}
        />
      </S.Row>
      <S.Row>
        <Typography variant="subtitle2" color="grey.500">
          The quoted FX rate for the transaction will be valid for 24 hours after confirmation. If funds are received
          after 24 hours, the FX rate at the time of fund receipt will be applied to your order.
        </Typography>
      </S.Row>
      <S.Row>
        <Button variant="contained" loading={loading} fullWidth onClick={handleClick} size="large">
          Confirm {isMint ? 'Mint' : 'Burn'}
        </Button>
      </S.Row>
      <Typography variant="subtitle2" textAlign="center">
        By clicking confirm you are authorizing SDC to process the transaction which may involve conversion of digital
        assets or fiat. You also understand the risks of cryptocurrency and digital wallets.{' '}
        <Link
          component="a"
          href="https://www.sedarplus.ca/csa-party/records/document.html?id=194b25dd3a2d2a8d59e108b3cf223b2fc41d68e33507899cbb350a659a9fea0a"
          target="_blank"
          rel="noopener"
        >
          Terms of service
        </Link>
      </Typography>
    </Modal>
  )
}

export default ConfirmRequest
